import { icon } from '../../components/icons';
import { SITE_CONFIG } from '../../config/site-config';
import { escapeHtml } from '../../lib/html';
import { $, requireElement } from '../../lib/dom';

export interface FieldProblem {
  readonly id: string;
  readonly label: string;
  readonly message: string;
}

/** Disables the submit button with a spinner. Returns a function that restores it. */
export function setSubmitting(form: HTMLFormElement, busyLabel: string): () => void {
  const button = requireElement('button[type="submit"]', form, HTMLButtonElement);
  const label = button.innerHTML;
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  button.innerHTML = `<span class="spinner" aria-hidden="true"></span> ${busyLabel}`;
  return () => {
    button.removeAttribute('aria-busy');
    button.disabled = false;
    button.innerHTML = label;
  };
}

function showSummary(summary: HTMLElement, html: string): void {
  summary.innerHTML = html;
  summary.hidden = false;
  summary.focus();
}

export function showProblems(summary: HTMLElement, problems: readonly FieldProblem[]): void {
  const heading =
    problems.length === 1
      ? 'There is 1 problem with this form'
      : `There are ${problems.length} problems with this form`;
  const items = problems
    .map(({ id, label, message }) => `<li><a href="#" data-focus="${id}">${escapeHtml(label)}: ${message}</a></li>`)
    .join('');
  showSummary(summary, `<h2>${heading}</h2><ul>${items}</ul>`);
}

/** Delivery failed. `emailHref` is a pre-filled mailto so the request is not lost. */
export function showDeliveryFailure(
  summary: HTMLElement,
  { emailHref, keptEntries }: { emailHref: string; keptEntries: boolean },
): void {
  const link = `<a href="${escapeHtml(emailHref)}">${SITE_CONFIG.SALES_EMAIL}</a>`;
  const text = keptEntries
    ? `Your details are still here. Please try again, or email ${link}.`
    : `Please email ${link}.`;
  showSummary(summary, `<h2>Something went wrong.</h2><ul><li>${text}</li></ul>`);
}

/** Replaces the security request form with its confirmation. */
export function showSecurityRequestSuccess(form: HTMLFormElement): void {
  const wrap = form.parentElement;
  form.outerHTML = `<div class="success" tabindex="-1" aria-live="polite"><span class="ok-ic">${icon('check')}</span><h2>Thank you. Our team will follow up about your security review.</h2></div>`;
  $('.success', wrap ?? document)?.focus();
}
